import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to handle tier limit exceeded (402) errors globally
 * 
 * Listens for the 'tierLimitExceeded' custom event dispatched by the API
 * interceptor and provides state + handlers for showing the ActionBlockedModal.
 * 
 * Usage:
 * ```jsx
 * function MyComponent() {
 *   const { 
 *     showBlockedModal, 
 *     setShowBlockedModal, 
 *     limitDetails,
 *     blockedAction 
 *   } = useTierLimitHandler();
 * 
 *   return (
 *     <>
 *       <ActionBlockedModal 
 *         open={showBlockedModal}
 *         onOpenChange={setShowBlockedModal}
 *         action={blockedAction}
 *         limitDetails={limitDetails}
 *       />
 *       // ... rest of component
 *     </>
 *   );
 * }
 * ```
 */
export function useTierLimitHandler() {
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [limitDetails, setLimitDetails] = useState({});
  const [blockedAction, setBlockedAction] = useState('perform this action');

  useEffect(() => {
    const handleTierLimitExceeded = (event) => {
      const detail = event.detail || {};
      setLimitDetails({
        currentVehicles: detail.currentVehicles,
        vehicleLimit: detail.vehicleLimit,
        currentTier: detail.currentTier,
        suggestedTier: detail.suggestedTier,
        currentTeamMembers: detail.currentTeamMembers,
        teamLimit: detail.teamLimit,
        suggestedLimits: detail.suggestedLimits,
        message: detail.message,
      });
      setBlockedAction(detail.blockedAction || 'perform this action');
      setShowBlockedModal(true);
    };

    window.addEventListener('tierLimitExceeded', handleTierLimitExceeded);
    
    return () => {
      window.removeEventListener('tierLimitExceeded', handleTierLimitExceeded);
    };
  }, []);

  // Helper to manually trigger the modal with custom action description
  const showLimitModal = useCallback((action, details = {}) => {
    setBlockedAction(action);
    setLimitDetails(prev => ({ ...prev, ...details }));
    setShowBlockedModal(true);
  }, []);

  return {
    showBlockedModal,
    setShowBlockedModal,
    limitDetails,
    blockedAction,
    showLimitModal,
  };
}

/**
 * Wrapper for API calls that need to show the blocked modal on 402 error
 * 
 * @param {function} apiCall - The API function to call
 * @param {string} actionDescription - Human-readable description of the action
 * @returns {function} Wrapped function that handles 402 errors
 */
export function withTierLimitHandling(apiCall, actionDescription) {
  return async (...args) => {
    try {
      return await apiCall(...args);
    } catch (error) {
      // If it's a 402 error, the API interceptor will dispatch the event
      // We just need to make sure the action description is passed
      if (error?.statusCode === 402 || error?.status === 402) {
        // Re-dispatch with the action description
        window.dispatchEvent(new CustomEvent('tierLimitExceeded', {
          detail: {
            ...error,
            blockedAction: actionDescription,
          }
        }));
      }
      throw error;
    }
  };
}

export default useTierLimitHandler;
